import React from "react";
import {
  Controller,
  type Control,
  type UseFormSetValue,
  type UseFormWatch,
} from "react-hook-form";
import {
  ProgramPaymentType,
  ProgramStudentType,
} from "../../../../types/dto/program";
import { CopyProgramSchedulePaymentStudentType } from "../../../../types/dto/scheduleStudent";
import { Input } from "../../../../shared/components/ui/form/inputs";
import { Select } from "antd";
import {
  programPaymentScheduleStudentAcademy,
  programsStudentAcademy,
} from "../../../../shared/services";
import useFilterProgramStudent from "../../hooks/programStudent/useFilterProgramStudent";
import {
  PAYMENT_CONCEPTS,
  isCosmetologyProgramName,
} from "../../../../shared/utils/academyPaymentConcepts";
import {
  filterAssignedSchedulesFromLastPayment,
  formatScheduleMonth,
} from "../../../academyProgramDetail/utils/scheduleDateUtils";

export default function PayStudentProgramFromStudentViewForm({
  control,
  programStudentByUserIdData,
  userId,
  watch,
  setValue,
}: {
  control?: Control<ProgramPaymentType & { payment_concept: string }>;
  programStudentByUserIdData: ProgramStudentType[];
  userId: number;
  watch: UseFormWatch<ProgramPaymentType & { payment_concept: string }>;
  setValue: UseFormSetValue<ProgramPaymentType & { payment_concept: string }>;
}) {
  const activeEnrollmentQuery = React.useMemo(
    () => ({ status: "active" as const }),
    []
  );

  const { setQuery, filteredData } =
    useFilterProgramStudent<ProgramStudentType>({
      service: programsStudentAcademy,
      initialData: programStudentByUserIdData,
      queryKey: "q",
      params: "user_id",
      parasValue: userId,
      skip: false,
      extraQuery: activeEnrollmentQuery,
    });

  const activeProgramOptions = React.useMemo(
    () => (filteredData || []).filter((item) => item.status === "active"),
    [filteredData]
  );

  const paymentConcept = watch("payment_concept") || PAYMENT_CONCEPTS.CUOTA;
  const selectedProgramId = watch("program_id");
  const isCuota = paymentConcept === PAYMENT_CONCEPTS.CUOTA;

  const selectedProgram = activeProgramOptions.find(
    (item) => String(item.program_id) === String(selectedProgramId)
  );
  const programName = selectedProgram?.program?.name;
  const showInternshipOption = isCosmetologyProgramName(programName);

  const [assignedSchedules, setAssignedSchedules] = React.useState<
    CopyProgramSchedulePaymentStudentType[]
  >([]);
  const [loadingSchedules, setLoadingSchedules] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    const loadAssignedSchedules = async () => {
      if (!selectedProgramId) {
        setAssignedSchedules([]);
        return;
      }

      setLoadingSchedules(true);
      try {
        const studentsRes = await programsStudentAcademy.find({
          query: {
            user_id: userId,
            program_id: +selectedProgramId,
            $limit: 10,
          },
        });

        const studentIds = (studentsRes.data || []).map(
          (student: { id: number }) => student.id
        );

        if (!studentIds.length) {
          if (!cancelled) {
            setAssignedSchedules([]);
          }
          return;
        }

        const schedulesRes = await programPaymentScheduleStudentAcademy.find({
          query: {
            program_student_id: { $in: studentIds },
            status: "active",
            $limit: 1000,
            $sort: { month: 1 },
          },
        });

        if (!cancelled) {
          setAssignedSchedules(schedulesRes.data || []);
        }
      } catch {
        if (!cancelled) {
          setAssignedSchedules([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingSchedules(false);
        }
      }
    };

    setValue("program_payment_schedule_id", undefined as never);
    loadAssignedSchedules();

    return () => {
      cancelled = true;
    };
  }, [selectedProgramId, userId, setValue]);

  const availableSchedules = React.useMemo(
    () => filterAssignedSchedulesFromLastPayment(assignedSchedules),
    [assignedSchedules]
  );

  const scheduleOptions =
    availableSchedules.length > 0
      ? availableSchedules.map((it) => ({
          key: `${it.payment_schedule_id}`,
          value: `${it.payment_schedule_id}`,
          label: formatScheduleMonth(
            it.month as string | Date,
            it.payment_due_date as string | Date,
            "YYYY / MMM"
          ),
        }))
      : [
          {
            key: "",
            value: "",
            label: loadingSchedules
              ? "Cargando fechas..."
              : "No hay cuotas pendientes asignadas",
          },
        ];

  const conceptOptions = [
    {
      value: PAYMENT_CONCEPTS.CUOTA,
      label: "Cuota mensual",
    },
    {
      value: PAYMENT_CONCEPTS.EXTRA_HOURS,
      label: "Horas adicionales",
    },
    ...(showInternshipOption
      ? [
          {
            value: PAYMENT_CONCEPTS.INTERNSHIP,
            label: "Pasantías",
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-3">
      <Controller
        control={control}
        name="program_id"
        render={({ field, fieldState }) => (
          <div>
            <label>Selecciona un programa</label>
            <Select
              {...field}
              showSearch
              style={{ width: 270 }}
              placeholder="Selecciona el programa"
              optionFilterProp="label"
              onSearch={(value) => setQuery(value)}
              options={
                activeProgramOptions.length > 0
                  ? activeProgramOptions.map((it) => ({
                      key: `${it.program_id}`,
                      value: `${it.program_id}`,
                      label: it.program?.name ?? `Programa #${it.program_id}`,
                    }))
                  : [
                      {
                        key: "",
                        value: "",
                        label: "No hay programas activos disponibles",
                      },
                    ]
              }
            />
            {fieldState.error ? (
              <span className="mt-1 block text-xs text-red-500">
                {fieldState.error.message}
              </span>
            ) : null}
          </div>
        )}
      />

      <Controller
        control={control}
        name="payment_concept"
        render={({ field, fieldState }) => (
          <div>
            <label>Concepto de pago</label>
            <Select
              {...field}
              style={{ width: 270 }}
              placeholder="Selecciona el concepto"
              options={conceptOptions}
            />
            {fieldState.error ? (
              <span className="mt-1 block text-xs text-red-500">
                {fieldState.error.message}
              </span>
            ) : null}
          </div>
        )}
      />

      {isCuota ? (
        <Controller
          control={control}
          name="program_payment_schedule_id"
          render={({ field, fieldState }) => (
            <div>
              <label>Selecciona la fecha de pago</label>
              <Select
                {...field}
                showSearch
                style={{ width: 270 }}
                placeholder="Selecciona la fecha de pago"
                optionFilterProp="label"
                loading={loadingSchedules}
                options={scheduleOptions}
              />
              {fieldState.error ? (
                <span className="mt-1 block text-xs text-red-500">
                  {fieldState.error.message}
                </span>
              ) : null}
            </div>
          )}
        />
      ) : null}

      <Input
        style={{ background: "none" }}
        control={control}
        label="Monto"
        name="amount"
        placeholder="100000"
        type="number"
      />
      <Input
        style={{ background: "none" }}
        control={control}
        label="Fecha de pago"
        name="payment_date"
        placeholder=""
        type="date"
      />
    </div>
  );
}
